import { MachineType } from '@internal/utils/machine/MachineType'
import { localeNamespace } from '@internal/utils/machine/i18n/Locale'
import { TextField } from '@renderer-ui-library/atoms'
import { Autocomplete } from '@renderer-ui-library/mui'
import { translations } from 'i18n/translations'
import { useTranslation } from 'next-i18next'
import React, { ChangeEvent, useCallback, useMemo } from 'react'
import { useMachineTypeName } from 'utils/machine/useMachineTypeName'
import { useCustomTags } from '../AutoComplete/useCustomTags'
import { SelectFilterOption } from '../RemoteAutocomplete/SelectFilterOption'
import {
  groupBy,
  renderGroup,
  useSelectItem,
} from '../RemoteAutocomplete/SelectItem'
import { areOptionsOfEqualValue } from '../RemoteAutocomplete/areOptionsOfEqualValue'
import { useMachineTypeOptions } from './useMachineTypeOptions'

type Props = {
  name?: string
  selectedMachineType: MachineType | null
  machineTypeSelectPlaceholder?: string
  onChange: (machineType: MachineType | null) => void
  onBlur?: (e: ChangeEvent) => void
  error?: string | boolean | undefined
  required?: true
}

export const MachineTypeSelectFilter: React.FC<Props> = React.memo(
  ({ name, onChange, selectedMachineType, error, required }) => {
    const { t } = useTranslation(localeNamespace.common)
    const { getMachineTypeName } = useMachineTypeName()
    const machineTypeOptions = useMachineTypeOptions()
    const { renderSelectItem } = useSelectItem()
    const { renderTags } = useCustomTags<SelectFilterOption, false>()
    const handleChangeMachineType = useCallback(
      (_: any, option: SelectFilterOption | null) =>
        onChange(option?.value ? (option?.value as MachineType) : null),
      [onChange]
    )

    const value = useMemo(
      (): SelectFilterOption => ({
        value: selectedMachineType ?? 'any',
        label: selectedMachineType
          ? getMachineTypeName(selectedMachineType, 'plural')
          : t(translations.machineTypePlaceholder).toString(),
      }),
      [getMachineTypeName, selectedMachineType, t]
    )

    return (
      <Autocomplete<SelectFilterOption, false>
        id='machine-type'
        options={machineTypeOptions}
        value={value}
        onChange={handleChangeMachineType}
        isOptionEqualToValue={areOptionsOfEqualValue}
        groupBy={groupBy}
        renderTags={renderTags}
        renderGroup={renderGroup}
        renderOption={renderSelectItem}
        renderInput={(params) => (
          <TextField
            name={name}
            {...params}
            placeholder={t(translations.searchResultsFiltersMachineTypeLabel)}
            size='small'
            error={!!error}
            helperText={error}
            required={required}
          />
        )}
        autoHighlight
        slotProps={{
          paper: {
            elevation: 10,
          },
        }}
      />
    )
  }
)

MachineTypeSelectFilter.displayName = 'MachineTypeSelectFilter'
